import React, { useEffect, useRef, useState } from 'react';
import Footer from '../components/Footer';
import Header from '../components/Header';
import Swal from 'sweetalert2';
import "../scss/card.css"
import { generateAxiosInstance } from '../hooks/useAuthorizedRequest';
import { generateAxiosInstanceCommon } from '../hooks/useAuthorizedReuestCommon';
import { useNavigate } from 'react-router-dom';
import { CModal } from '@coreui/react';
import { generateAxiosInstanceAdminFileUpload } from '../hooks/useAuthorizedRequestForFileUpload';
import { Loader } from '../components/ModalsCommonFunctions';

const UploadApp = () => {
    const authorizedAxios = generateAxiosInstance()
    const authorizedAxiosAdminFileUpload = generateAxiosInstanceAdminFileUpload()
    const accessToken = localStorage.getItem('accessToken');

    const initialFormState = {
        appName: '',
        publisherName: '',
        apkPath: '',
        apkIconPath: '',
        isInstitute: false,
        userType: '',
        userUniqueId: '',
        userId: '',
        password: ''
    }

    const [appData, setAppData] = useState(initialFormState)
    const [isVerified, setIsVerified] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const inputApkFile = useRef(null);
    const inputApkIconFile = useRef(null);

    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    const handleNavigate = (route) => {
        navigate(route);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        if (name === 'userUniqueId') {
            setIsVerified(false);
        }
        setAppData((prevData) => ({
            ...prevData,
            [name]: value
        }));
    }

    const handleCheckboxChange = () => {
        setAppData((prevData) => {
            return {
                ...prevData,
                isInstitute: !prevData.isInstitute
            }
        });
    };

    const handleVerifyUser = (e) => {
        if (validateFormVerify()) {
            setLoading(true);
            const apiUrl = `/verify-user?userUniqueId=${appData.userUniqueId}`;
            authorizedAxios && authorizedAxios.get(apiUrl)
                .then(response => {
                    setAppData((prevData) => {
                        return {
                            ...prevData,
                            userId: response.data.result.id
                        }
                    })

                    setIsVerified(true);
                    Swal.fire({
                        text: response.data.message
                    })
                })
                .catch(error => {
                    const errorMsg = error?.response?.data?.message || 'Something went wrong!';
                    Swal.fire({
                        text: errorMsg
                    })
                })
                .finally(() => {
                    setLoading(false); // End loading
                });
        } else {
            const newError = {};
            for (const [key, value] of Object.entries(appData)) {
                if (key !== "userId") {
                    newError[key] = value === '' ? 'This field is required.' : '';
                }
            }
            setError(newError);
        }
    }

    const handleUploadApkFile = async (e) => {
        e.preventDefault();
        const file = e.target.files[0];
        if (!file) {
            setAppData((prevData) => {
                return {
                    ...prevData,
                    apkPath: ''
                }
            })
            return; // Exit function if no file is selected
        }

        const fileExtension = file.name.split(".").pop().toLowerCase();
        const allowedExtensions = ["apk"];
        const body = new FormData();
        body.append("file", file);

        if (allowedExtensions.includes(fileExtension)) {
            setLoading(true);
            const apiUrl = "upload-apk-file";
            authorizedAxiosAdminFileUpload && authorizedAxiosAdminFileUpload.post(apiUrl, body)
                .then((res) => {
                    setAppData((prevData) => {
                        return {
                            ...prevData,
                            [e.target.name]: res.data.result
                        };
                    });
                    Swal.fire({
                        text: res.data.message
                    })
                })
                .catch((err) => {
                    Swal.fire({ text: "An error occurred while uploading the file" });
                })
                .finally(() => {
                    setLoading(false); // End loading
                });
        } else {
            e.target.value = '';
            Swal.fire({ text: "Only APK files are supported" });
        }
    };

    const handleUploadApkIcon = async (e) => {
        const file = e.target.files[0];
        if (!file) {
            setAppData((prevData) => {
                return {
                    ...prevData,
                    apkIconPath: ''
                }
            })
            return;
        }

        const fileExtension = file.name.split(".").pop().toLowerCase();
        const allowedExtensions = ["jpeg", "jpg", "png", "webp", "svg", "JPEG", "JPG", "PNG", "WEBP", "SVG"];

        const body = new FormData();
        body.append("file", file);

        if (allowedExtensions.includes(fileExtension)) {
            setLoading(true);
            const apiUrl = "upload-apk-file-icon";
            authorizedAxiosAdminFileUpload && authorizedAxiosAdminFileUpload.post(apiUrl, body)
                .then((res) => {
                    setAppData((prevData) => {
                        return {
                            ...prevData,
                            [e.target.name]: res.data.result
                        };
                    });
                    Swal.fire({
                        text: res.data.message
                    })
                })
                .catch((err) => {
                    Swal.fire({ text: "An error occurred while uploading the file" });
                })
                .finally(() => {
                    setLoading(false); // End loading
                });
        } else {
            Swal.fire({ text: "Only JPEG, PNG, WEBP, and SVG files are supported" });
        }
    };

    //error
    const initialErrorState = {

    }

    const [error, setError] = useState(initialErrorState)

    const validateForm = () => {
        const fieldsToExclude = appData.isInstitute ? [] : ['userType', 'userUniqueId', 'userId', 'password'];

        const isValid = Object.entries(appData).filter(([key]) => !fieldsToExclude.includes(key))
            .every(([key, value]) => typeof value === "string" ? value.trim() !== '' : value.length !== 0);

        const updatedError = {};
        for (const [key, value] of Object.entries(error)) {
            if (!fieldsToExclude.includes(key) || appData.isInstitute) {
                updatedError[key] = value;
            }
        }

        const noErrors = Object.values(updatedError).every((error) => error === '');
        setError(updatedError);

        return isValid && noErrors;
    };

    const validateFormVerify = () => {
        const errorValues = Object.keys(error)
            .filter(key => key !== "userId")
            .map(key => error[key]);

        const appDataValues = Object.keys(appData)
            .filter(key => key !== "userId")
            .map(key => appData[key]);

        return errorValues.every(error => error === '') &&
            appDataValues.every(value => typeof (value) === "string" ? value !== '' : value.length !== 0);
    };


    useEffect(() => {
        const fieldsToExclude = appData.isInstitute ? [] : ['userType', 'userUniqueId', 'userId', 'password'];

        const updatedError = {};
        for (const [key, value] of Object.entries(error)) {
            if (!fieldsToExclude.includes(key) || appData.isInstitute) {
                updatedError[key] = value;
            }
        }

        if (!Object.values(updatedError).every((error) => error === '')) {
            for (const [key, value] of Object.entries(appData)) {
                if (!fieldsToExclude.includes(key)) {
                    if (value === '' || value.length === 0) {
                        updatedError[key] = 'This field is required.';
                    } else {
                        updatedError[key] = '';
                    }
                }
            }
        }

        setError(updatedError);
    }, [appData]);

    const uploadApp = () => {
        const apiUrl = `/add-apk`;
        if (validateForm()) {
            setLoading(true);
            let data = {
                ...appData
            }

            if (appData.isInstitute == false && ((appData.userUniqueId != "" && appData.userUniqueId.length > 0) || (appData.userId != "" && appData.userId.length > 0) || (appData.password != "" && appData.password.length > 0) || (appData.userType != "" && appData.userType.length > 0))) {
                data = {
                    ...data,
                    userType: '',
                    userUniqueId: '',
                    userId: '',
                    password: ''
                }
            }

            authorizedAxios && authorizedAxios.post(apiUrl, data)
                .then(response => {
                    setAppData(initialFormState)
                    inputApkFile.current.value = '';
                    inputApkIconFile.current.value = '';
                    setIsVerified(false);
                    setError(initialErrorState);
                    Swal.fire(
                        { text: response.data.message }
                    );
                })
                .catch(error => {
                    const errorMsg = error?.response?.data?.message || 'Something went wrong!';
                    Swal.fire(
                        { text: errorMsg }
                    );
                })
                .finally(() => {
                    setLoading(false); // End loading
                });
        } else {
            const newError = {};
            const fieldsToExclude = appData.isInstitute ? [] : ['userType', 'userUniqueId', 'userId', 'password'];
            for (const [key, value] of Object.entries(appData)) {
                if (!fieldsToExclude.includes(key)) {
                    newError[key] = value === '' ? 'This field is required.' : '';
                }
            }
            setError(newError);
        }
    }

    return (
        <>
            <Loader loading={loading} />
            <div className="wrapper d-flex flex-column min-vh-100 bg-light">
                <Header />
                <div className="container-fluid">
                    <nav aria-label="breadcrumb">
                        <ol className="breadcrumb my-0 ms-2">
                            <li className="breadcrumb-item dashboard" onClick={() => handleNavigate('/dashboard')}>
                                <span>Dashboard</span>
                            </li>
                            <li className="breadcrumb-item active"><span>Upload App </span></li>
                        </ol>
                    </nav>
                </div>
                <div className="container-lg">
                    <div className="row">
                        <div className="card border-info mx-auto mt-3">
                            <div className="card-content collpase show">
                                <div className="card-header" style={{ backgroundColor: '#3081D0', borderRadius: 0 }}>
                                    <h4 className="text-white"><b><span className="fa fa-file-o"></span><i class="fa-brands fa-app-store"></i> Upload App</b></h4>
                                </div>
                                <div className="card-body ">
                                    <div className="row">
                                        <div className="col-md-12">
                                            <div className="row">
                                                <div className="col-md-6">
                                                    <label for="appName" className="col-form-label"><span style={{ color: "red" }}>*</span>Name of App :</label>
                                                    <input type="text" className="form-control" name="appName" value={appData.appName} onChange={handleInputChange} />
                                                    {error.appName && <p style={{ color: 'red' }}>{error.appName
                                                    }</p>}
                                                </div>

                                                <div className="col-md-6">
                                                    <label for="publisherName " className="col-form-label"><span style={{ color: "red" }}>*</span>Publisher :</label>
                                                    <input type="text" className="form-control" name="publisherName" value={appData.publisherName} onChange={handleInputChange} />
                                                    {error.publisherName && <p style={{ color: 'red' }}>{error.publisherName}</p>}
                                                </div>
                                            </div>

                                            <div className="row">
                                                <div className="col-md-6">
                                                    <label for="apkPath" className="col-form-label"><span style={{ color: "red" }}>*</span>APK Path : {/*<span style={{ color: "red" }}>only xlsx file supported </span>*/}</label>
                                                    <input required type="file" className="form-control" name="apkPath" onChange={handleUploadApkFile} ref={inputApkFile} />
                                                    {error.apkPath && <p style={{ color: 'red' }}>{error.apkPath}</p>}

                                                </div>
                                                <div className="col-md-6">
                                                    <label for="apkIconPath" className="col-form-label"><span style={{ color: "red" }}>*</span>Upload Icon :</label>
                                                    <input required type="file" className="form-control" name="apkIconPath" onChange={handleUploadApkIcon} ref={inputApkIconFile} />
                                                    {error.apkIconPath && <p style={{ color: 'red' }}>{error.apkIconPath}</p>}
                                                </div>
                                            </div>

                                            <div className='row'>
                                                <div className="col-md-6">
                                                    <label for="isInstitute" className="col-form-label">Is Institute App :</label>
                                                    <input type="checkbox" name="isInstitute" /*defaultValue={false}*/ checked={appData?.isInstitute} style={{
                                                        marginLeft: '20px', width: '18px',
                                                        height: '16px',
                                                        accentColor: 'green', marginTop: '20px',
                                                    }} onChange={handleCheckboxChange} />
                                                </div>
                                            </div>

                                            {appData.isInstitute && <div className='row'>
                                                <div className="col-md-6">
                                                    <label for="userType" className="col-form-label"><span style={{ color: "red" }}>*</span>For :</label>
                                                    <select required class="form-select" aria-label="Default select example" name='userType' onChange={handleInputChange} value={appData.userType}>
                                                        <option selected value={''} >--Select--</option>
                                                        {/* <option value='I'>Institute</option> */}
                                                        {/* <option value='B'>Section</option> */}
                                                        <option value='T'>Teacher</option>
                                                        <option value='S'>Student</option>
                                                    </select>
                                                    {error.userType && <p style={{ color: 'red' }}>{error.userType}</p>}
                                                </div>

                                                <div className="col-md-6">
                                                    <label for="password" className="col-form-label"><span style={{ color: "red" }}>*</span>Set App Password :</label>
                                                    <div className="input-group password_field">
                                                        <input type={showPassword ? "text" : "password"} className="form-control" name="password" value={appData.password} onChange={handleInputChange} />
                                                        {showPassword ? (
                                                            <button><i className="fa fa-eye" aria-hidden="true" onClick={() => setShowPassword(!showPassword)}></i></button>
                                                        ) : (
                                                            <button><i className="fa fa-eye-slash" onClick={() => setShowPassword(!showPassword)}></i></button>
                                                        )}
                                                    </div>
                                                    {error.password && <p style={{ color: 'red' }}>{error.password}</p>}
                                                </div>
                                            </div>}

                                            {appData.isInstitute && <div className='row'>
                                                <div className='col-md-6'>
                                                    <label for="userUniqueId" className="col-form-label"><span style={{ color: "red" }}>*</span>Institute Id :</label>

                                                    <div className='vefify_field'>
                                                        <input type="text" className="form-control" name="userUniqueId" value={appData.userUniqueId} onChange={handleInputChange} />

                                                        {isVerified === false && <button className="btn btn-primary" type="button" style={{ marginRight: '20px' }} onClick={handleVerifyUser}>Verify</button>}
                                                        {isVerified === true && <button className="btn btn-success" type="button" style={{ marginRight: '20px' }}><i class="fa-solid fa-check"></i></button>}
                                                    </div>
                                                    {error.userUniqueId && <p style={{ color: 'red' }}>{error.userUniqueId}</p>}

                                                </div>
                                            </div>}

                                            <div className="row">
                                                <div className="col-md-12 mt-3 mb-1" style={{ textAlign: 'center' }}>
                                                    <div className="col-auto">
                                                        <button className="btn btn-primary" type="button" style={{ marginRight: '20px' }} disabled={(appData.isInstitute ? (isVerified == false) : appData.isInstitute)} onClick={uploadApp}>Upload</button>
                                                        <button className="btn btn-danger delete_icon" type="button" style={{ marginRight: "20px" }} onClick={() => { setAppData(initialFormState); inputApkFile.current.value = ''; inputApkIconFile.current.value = '' }}>Reset</button>
                                                    </div>
                                                </div>
                                            </div>

                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <Footer />
        </>
    )
};

export default UploadApp;